<script setup lang="ts">
import type { DropdownMenuItem } from '@nuxt/ui'

const props = withDefaults(defineProps<{
  modelValue: number | null
  persistKey?: string
  buttonColor?: 'secondary' | 'tertiary' | 'primary' | 'neutral'
  buttonLabel?: string
}>(), {
  buttonColor: 'secondary',
})

const emit = defineEmits<{
  'update:modelValue': [value: number | null]
  'change': [value: number | null]
}>()

const cacheStore = useCacheStore()
const baseStore = useBaseStore()
const localePath = useLocalePath()
const { getSortedAisles, getDisplayAisleMeta, OTHER_AISLE_ID } = useStoreAisles()
const { isExtraUser, showExtraFeatureToast } = useExtraFeature()

type AisleFilterMenuItem = DropdownMenuItem & {
  aisleId?: number
  extraSection?: boolean
}

const isOpen = ref(false)

const displayButtonLabel = computed(() => props.buttonLabel ?? 'Filtrer par allée')

const menuItems = computed((): AisleFilterMenuItem[][] => {
  const allAislesItem: AisleFilterMenuItem = {
    label: 'Toutes',
    type: 'checkbox',
    checked: props.modelValue == null,
    onUpdateChecked(checked: boolean) {
      if (checked) {
        selectAisle(null)
      }
      else {
        closeMenu()
      }
    },
    onSelect(e: Event) {
      e.preventDefault()
    },
  }

  const sectionLabel: AisleFilterMenuItem = {
    type: 'label',
    label: 'Filtrer par allée',
    extraSection: true,
  }

  const aisleItems: AisleFilterMenuItem[] = getSortedAisles().map((aisle) => {
    if (isExtraUser.value) {
      return {
        label: aisle.label,
        aisleId: aisle.id,
        type: 'checkbox',
        checked: props.modelValue === aisle.id,
        onUpdateChecked(checked: boolean) {
          if (checked) {
            selectAisle(aisle.id)
          }
          else {
            closeMenu()
          }
        },
        onSelect(e: Event) {
          e.preventDefault()
        },
      }
    }

    return {
      label: aisle.label,
      aisleId: aisle.id,
      class: 'opacity-60',
      trailingIcon: 'i-mdi-lock',
      onSelect: () => {
        showExtraFeatureToast('Le filtre par allée est disponible uniquement avec un abonnement Extra.')
      },
    }
  })

  return [[
    allAislesItem,
    { type: 'separator' },
    sectionLabel,
    ...aisleItems,
  ]]
})

function persistAisleCache(aisleId: number | null) {
  if (props.persistKey && baseStore.showAisles) {
    cacheStore.setCache(props.persistKey, aisleId)
  }
}

function closeMenu() {
  nextTick(() => {
    isOpen.value = false
  })
}

function selectAisle(aisleId: number | null) {
  emit('update:modelValue', aisleId)
  persistAisleCache(aisleId)
  emit('change', aisleId)
  closeMenu()
}

function resetAisleIfNotExtra() {
  if (!isExtraUser.value && props.modelValue != null) {
    selectAisle(null)
  }
}

watch(() => props.modelValue, (value) => {
  persistAisleCache(value)
})

watch(() => baseStore.showAisles, (show) => {
  if (!show && props.persistKey) {
    cacheStore.clearCache(props.persistKey)
    if (props.modelValue != null) {
      emit('update:modelValue', null)
    }
  }
})

watch(isExtraUser, () => {
  resetAisleIfNotExtra()
}, { immediate: true })
</script>

<template>
  <UDropdownMenu
    v-model:open="isOpen"
    :items="menuItems"
    checked-icon="i-mdi-check"
    :content="{ align: 'start', sideOffset: 1 }"
    :ui="{ content: 'w-72 max-h-80 overflow-y-auto' }"
    class="group"
  >
    <UButton
      trailing-icon="i-mdi-menu-down"
      :color="buttonColor"
      :ui="{
        trailingIcon: 'group-data-[state=open]:rotate-180 transition-transform duration-200',
      }"
    >
      {{ displayButtonLabel }}
      <UBadge v-if="modelValue != null" size="sm" variant="soft" color="white">
        1
      </UBadge>
    </UButton>
    <template #item-label="{ item }">
      <span v-if="item.extraSection" class="block min-w-0 w-full text-left">
        <span class="flex items-center">
          <span>{{ item.label }}</span>
          <span class="extra-badge-gold">Extra</span>
        </span>
        <p v-if="!isExtraUser" class="mt-1 text-left text-xs font-normal text-gray-400">
          Le filtre par allée est réservé aux membres
          <ULink :to="localePath({ name: 'ExtraPage' })" class="text-primary underline">Extra</ULink>.
        </p>
      </span>
      <span v-else class="flex items-center gap-2 min-w-0">
        <UIcon
          v-if="item.aisleId === OTHER_AISLE_ID"
          name="i-mdi-close"
          class="size-2.5 shrink-0 text-poivre-500"
        />
        <span
          v-else-if="item.aisleId != null"
          class="inline-block size-2.5 rounded-full shrink-0"
          :style="{ backgroundColor: getDisplayAisleMeta(item.aisleId).backgroundColor }"
        />
        <span class="truncate">{{ item.label }}</span>
      </span>
    </template>
  </UDropdownMenu>
</template>
